import { useEffect, useState } from 'react';
import { useSearch as useSearchParams, useNavigate, useMatch } from '@tanstack/react-router';
import { SearchFacet, SearchSortOrder, FilterSet } from '#server/models';
import { allFilter, courseFilter, eventsFilter, newsFilter, peopleFilter, researchFilter } from '#server/data';
import { useDebounce, useSearch, useSuggestions } from '@/hooks';
import {
  useFilterActions,
  useFilterStore,
  useSortOrderActions,
  useSortOrderStore,
  useAvailabilityActions,
  useAvailabilityStore,
} from '@/store';
import {
  allSortOrders,
  courseSortOrders,
  eventsSortOrders,
  newsSortOrders,
  peopleSortOrders,
  researchSortOrders,
} from '@/data';
import { SearchContext } from './SearchContext';

const facetToFilterMap: Record<SearchFacet, FilterSet> = {
  all: allFilter,
  courses: courseFilter,
  events: eventsFilter,
  news: newsFilter,
  people: peopleFilter,
  research: researchFilter,
};

const facetToSortOrderMap: Record<SearchFacet, SearchSortOrder[]> = {
  all: allSortOrders,
  courses: courseSortOrders,
  events: eventsSortOrders,
  news: newsSortOrders,
  people: peopleSortOrders,
  research: researchSortOrders,
};

const initialPageState: Record<SearchFacet, number> = {
  all: 1,
  courses: 1,
  events: 1,
  news: 1,
  people: 1,
  research: 1,
};

export const SearchProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const searchParams = useSearchParams({ from: '__root__' });
  const routeParams = useMatch({ from: '/results/$facet', shouldThrow: false });

  const searchQuery = searchParams.q ?? '';

  const defaultFacet = routeParams?.params?.facet ?? 'all';

  const { activeFilters, region } = useFilterStore();
  const { setCurrentFacet: setCurrentFilterFacet, toggleFilterOption } = useFilterActions();
  const { activeSortOrder } = useSortOrderStore();
  const { setCurrentFacet: setCurrentSortOrderFacet } = useSortOrderActions();
  const { activeAvailability } = useAvailabilityStore();
  const { setCurrentFacet: setCurrentAvailabilityFacet } = useAvailabilityActions();

  const [suggestionsQuery, setSuggestionsQuery] = useState(searchQuery);
  const debouncedSuggestionsQuery = useDebounce(suggestionsQuery, 500);

  const [facet, setFacet] = useState<SearchFacet>(defaultFacet);

  const [pages, setPages] = useState<Record<SearchFacet, number>>(initialPageState);

  useEffect(() => {
    const filterSet = facetToFilterMap[facet];
    const sortOrderSet = facetToSortOrderMap[facet];
    if (filterSet) {
      setCurrentFilterFacet(facet, filterSet);
      setCurrentSortOrderFacet(facet, sortOrderSet);
      setCurrentAvailabilityFacet(facet);
    }
  }, [facet, setCurrentFilterFacet, setCurrentSortOrderFacet, setCurrentAvailabilityFacet, region]);

  // Use filters from URL parameters on initial load (runs AFTER facet is set)
  useEffect(() => {
    const filterSet = facetToFilterMap[facet];
    // Don't run if no filter mapping or some filters are already set
    if (!filterSet || Object.keys(activeFilters).length > 0) return;

    // Extract filter params from URL (exclude 'q')
    const { q, ...filterParams } = searchParams;
    const urlFilterKeys = Object.keys(filterParams);

    // If no URL filters, then return
    if (urlFilterKeys.length === 0) return;

    // Mapping URL params to filter store format
    urlFilterKeys.forEach((urlKey) => {
      const filterConfig = filterSet.find((f) => f.metadataField === urlKey);
      if (!filterConfig) {
        console.warn(`[DeepLink] No filter config found for: ${urlKey}`);
        return;
      }

      if (filterConfig.type === 'slider' || filterConfig.type === 'date-range') {
        console.warn(`[DeepLink] Filter url matching does not support ${filterConfig.type} type: ${urlKey}`);
        return;
      }

      const urlValue = filterParams[urlKey];
      const values = Array.isArray(urlValue) ? urlValue : [urlValue];

      // Applying each value as a filter option
      values.forEach((value) => {
        if (typeof value === 'string') {
          // Finding the option that matches the URL value (case-insensitive)
          const matchingOption = filterConfig.options?.find((opt) => {
            const optValue = opt.metadataValue;
            return optValue && optValue.toLowerCase() === value.toLowerCase();
          });
          if (matchingOption) {
            toggleFilterOption(filterConfig.title, matchingOption.title);
          } else {
            console.warn(`[DeepLink] No matching option for ${urlKey}=${value}`);
          }
        }
      });
    });
  }, []); //intentionally left empty so this only runs once after initial load

  useEffect(() => {
    setPages(initialPageState);
  }, [activeFilters, activeSortOrder, activeAvailability, searchQuery, facet]);

  const setPage = (facet: SearchFacet, page: number) => {
    setPages({ ...pages, [facet]: page });
  };

  const setSearchQuery = (query: string) => {
    navigate({
      to: '.',
      search: (prev) => ({ ...prev, q: query }),
    });
  };

  const setSearchAndSuggestionQuery = (query: string) => {
    setSearchQuery(query);
    setSuggestionsQuery(query);
  };

  const filterReady = useFilterStore((s) => s.currentFacet) === facet;
  const sortReady = useSortOrderStore((s) => s.currentFacet) === facet;
  const availabilityReady = useAvailabilityStore((s) => s.currentFacet) === facet;
  const storeReady = filterReady && sortReady && availabilityReady;

  const { results: searchResults, error } = useSearch({
    searchTerms: searchQuery,
    facet,
    page: pages[facet],
    enabled: storeReady,
  });

  const { results: suggestionResults } = useSuggestions({ searchTerms: debouncedSuggestionsQuery, facet, size: 5 });

  // clear previous suggestions as soon as we get a new suggetion query,
  // and wait until we get the new results from the debounced query before returning anything
  const deboundedSuggestionResults =
    suggestionsQuery === debouncedSuggestionsQuery ? suggestionResults : { results: [] };

  return (
    <SearchContext.Provider
      value={{
        suggestionsQuery,
        setSuggestionsQuery,
        setSearchQuery,
        setSearchAndSuggestionQuery,
        searchQuery,
        facet,
        setFacet,
        setPage,
        suggestionResults: deboundedSuggestionResults,
        searchResults: searchResults,
        loading: !storeReady || (searchResults?.loading ?? false),
        error,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};
